// ---------------------------------------------------------------------------
// Root error boundary — turn a white-screen into a recoverable event.
// ---------------------------------------------------------------------------
// Before this there was NO boundary anywhere: a single render exception blanked
// the entire app for that user, with nothing logged server-side. Now a render
// crash shows a recover UI AND POSTs the stack to /api/v1/client-error so the
// failure is visible in the server logs (and the alert webhook) instead of
// silently costing someone their session.
// ---------------------------------------------------------------------------

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { crashed: boolean; message: string }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false, message: '' };

  static getDerivedStateFromError(err: Error): State {
    return { crashed: true, message: err.message || 'Something went wrong.' };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // Report to the server sink. Fire-and-forget, no auth header needed — a
    // crashed session may not have a usable token, and we'd rather log it than
    // lose it to a 401. Never let the reporter itself throw.
    try {
      const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';
      void fetch(`${base}/client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: err.message,
          stack: err.stack?.slice(0, 4000),
          componentStack: info.componentStack?.slice(0, 2000),
          url: window.location.href,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* reporting is best-effort */ }
  }

  render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-sidebar px-6 text-center">
        <div className="text-3xl">😵‍💫</div>
        <div className="text-lg font-semibold text-ink">Something broke on this screen</div>
        <p className="max-w-sm text-sm text-muted">
          It has been reported automatically. Reloading usually fixes it — your work is saved on the server.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-page transition hover:brightness-110"
        >
          Reload
        </button>
        <p className="max-w-md break-words text-[11px] text-faint">{this.state.message}</p>
      </div>
    );
  }
}
